if (!customElements.get('product-fullscreen-slider')) {
  class ProductFullscreenSlider extends HTMLElement {
    constructor() {
      super();
      this.track = this.querySelector('[data-track]');
      this.slides = Array.from(this.querySelectorAll('.product-fullscreen-slider__slide'));
      this.prevBtn = this.querySelector('.product-fullscreen-slider__nav--prev');
      this.nextBtn = this.querySelector('.product-fullscreen-slider__nav--next');
      this.closeBtn = this.querySelector('.product-fullscreen-slider__close');
      this.currentEl = this.querySelector('[data-current]');
      this.totalEl = this.querySelector('[data-total]');

      this.currentIndex = 0;
      this.isDragging = false;
      this.startX = 0;
      this.currentX = 0;
      this.activePointerId = null;

      this._onKeyDown = this._onKeyDown.bind(this);
      this._onPointerDown = this._onPointerDown.bind(this);
      this._onPointerMove = this._onPointerMove.bind(this);
      this._onPointerUp = this._onPointerUp.bind(this);

      this.prevBtn && this.prevBtn.addEventListener('click', () => this.goTo(this.currentIndex - 1));
      this.nextBtn && this.nextBtn.addEventListener('click', () => this.goTo(this.currentIndex + 1));
      this.closeBtn && this.closeBtn.addEventListener('click', () => this.hide());

      if (this.track) this.track.addEventListener('pointerdown', this._onPointerDown);

      this.slides.forEach((slide) => {
        const videoWrapper = slide.querySelector('[data-video]');
        if (!videoWrapper) return;
        const poster = videoWrapper.querySelector('.product-fullscreen-slider__poster');
        if (poster) poster.addEventListener('click', () => this._loadVideo(slide));
      });

      if (this.totalEl) this.totalEl.textContent = this.slides.length;
    }

    connectedCallback() {
      if (this.moved) return;
      this.moved = true;
      document.body.appendChild(this);
    }

    show(opener) {
      this.openedBy = opener;
      const openerMediaId = opener && opener.getAttribute ? opener.getAttribute('data-media-id') : null;
      let idx = 0;
      if (openerMediaId) {
        const exact = this.slides.findIndex((s) => s.getAttribute('data-media-id') === openerMediaId);
        if (exact >= 0) {
          idx = exact;
        } else {
          const byRaw = this.slides.findIndex((s) => s.getAttribute('data-raw-media-id') === openerMediaId);
          if (byRaw >= 0) idx = byRaw;
        }
      }

      document.body.classList.add('overflow-hidden');
      this.setAttribute('open', '');
      this.setAttribute('aria-hidden', 'false');
      document.addEventListener('keydown', this._onKeyDown);
      this.goTo(idx, false);

      const dialog = this.querySelector('[tabindex="-1"]') || this;
      try { dialog.focus({ preventScroll: true }); } catch (e) { dialog.focus(); }
    }

    hide() {
      this.removeAttribute('open');
      this.setAttribute('aria-hidden', 'true');
      document.body.classList.remove('overflow-hidden');
      document.body.dispatchEvent(new CustomEvent('modalClosed'));
      document.removeEventListener('keydown', this._onKeyDown);
      this._pauseAllVideos(true);

      if (this.openedBy && typeof this.openedBy.focus === 'function') {
        try { this.openedBy.focus({ preventScroll: true }); } catch (e) { this.openedBy.focus(); }
      }
    }

    goTo(index, animate = true) {
      if (!this.slides.length) return;
      if (index < 0) index = 0;
      if (index >= this.slides.length) index = this.slides.length - 1;

      const previousIndex = this.currentIndex;
      this.currentIndex = index;

      if (this.track) {
        this.track.style.transition = animate ? 'transform 0.35s cubic-bezier(0.22, 0.61, 0.36, 1)' : 'none';
        this.track.style.transform = `translateX(-${index * 100}%)`;
      }

      if (this.currentEl) this.currentEl.textContent = index + 1;

      if (this.prevBtn) this.prevBtn.toggleAttribute('disabled', index === 0);
      if (this.nextBtn) this.nextBtn.toggleAttribute('disabled', index === this.slides.length - 1);

      this.slides.forEach((s, i) => s.classList.toggle('is-active', i === index));

      if (previousIndex !== index) this._pauseSlideMedia(this.slides[previousIndex]);
    }

    _loadVideo(slide) {
      const videoWrapper = slide.querySelector('[data-video]');
      if (!videoWrapper) return;

      if (videoWrapper.getAttribute('data-loaded')) {
        const existingVideo = videoWrapper.querySelector('video');
        if (existingVideo) {
          existingVideo.play().catch(() => {});
          return;
        }
        const existingIframe = videoWrapper.querySelector('iframe');
        if (existingIframe) this._playIframe(existingIframe);
        return;
      }

      const template = videoWrapper.querySelector('template');
      if (!template || !template.content.firstElementChild) return;

      const node = template.content.firstElementChild.cloneNode(true);
      videoWrapper.appendChild(node);
      videoWrapper.setAttribute('data-loaded', 'true');

      const poster = videoWrapper.querySelector('.product-fullscreen-slider__poster');
      if (poster) poster.style.display = 'none';

      if (node.tagName === 'VIDEO') {
        node.setAttribute('playsinline', '');
        node.play().catch(() => {});
      } else if (node.tagName === 'IFRAME') {
        this._playIframe(node);
      }
    }

    _playIframe(iframe) {
      const src = iframe.getAttribute('src') || '';
      if (!src) return;
      if (/autoplay=1/.test(src)) return;
      const separator = src.includes('?') ? '&' : '?';
      iframe.setAttribute('src', src + separator + 'autoplay=1');
    }

    _pauseSlideMedia(slide) {
      if (!slide) return;
      slide.querySelectorAll('video').forEach((v) => {
        try { v.pause(); } catch (e) {}
      });
      slide.querySelectorAll('iframe').forEach((iframe) => {
        const src = iframe.getAttribute('src');
        if (src) iframe.setAttribute('src', src.replace(/([?&])autoplay=1(&?)/, (m, p1, p2) => (p2 ? p1 : '')));
      });
    }

    _pauseAllVideos(resetPoster = false) {
      this.slides.forEach((slide) => {
        this._pauseSlideMedia(slide);
        if (resetPoster) {
          const wrapper = slide.querySelector('[data-video]');
          if (!wrapper) return;
          const poster = wrapper.querySelector('.product-fullscreen-slider__poster');
          const media = wrapper.querySelector('video, iframe');
          if (media) media.remove();
          wrapper.removeAttribute('data-loaded');
          if (poster) poster.style.display = '';
        }
      });
    }

    _onKeyDown(e) {
      if (!this.hasAttribute('open')) return;
      if (e.key === 'Escape') {
        e.preventDefault();
        this.hide();
      } else if (e.key === 'ArrowLeft') {
        e.preventDefault();
        this.goTo(this.currentIndex - 1);
      } else if (e.key === 'ArrowRight') {
        e.preventDefault();
        this.goTo(this.currentIndex + 1);
      }
    }

    _onPointerDown(e) {
      if (e.target.closest('button, video, iframe, model-viewer, a')) return;
      if (e.pointerType === 'mouse' && e.button !== 0) return;

      this.isDragging = true;
      this.startX = e.clientX;
      this.currentX = e.clientX;
      this.activePointerId = e.pointerId;
      if (this.track) {
        this.track.style.transition = 'none';
        try { this.track.setPointerCapture(e.pointerId); } catch (err) {}
        this.track.addEventListener('pointermove', this._onPointerMove);
        this.track.addEventListener('pointerup', this._onPointerUp);
        this.track.addEventListener('pointercancel', this._onPointerUp);
      }
    }

    _onPointerMove(e) {
      if (!this.isDragging || e.pointerId !== this.activePointerId) return;
      this.currentX = e.clientX;
      const dx = this.currentX - this.startX;
      const width = this.clientWidth || 1;
      const percent = (dx / width) * 100;
      if (this.track) {
        this.track.style.transform = `translateX(calc(-${this.currentIndex * 100}% + ${percent}%))`;
      }
    }

    _onPointerUp(e) {
      if (!this.isDragging) return;
      this.isDragging = false;
      this.activePointerId = null;
      if (this.track) {
        this.track.removeEventListener('pointermove', this._onPointerMove);
        this.track.removeEventListener('pointerup', this._onPointerUp);
        this.track.removeEventListener('pointercancel', this._onPointerUp);
      }
      const dx = this.currentX - this.startX;
      const width = this.clientWidth || 1;
      const threshold = width * 0.12;
      if (dx > threshold) this.goTo(this.currentIndex - 1);
      else if (dx < -threshold) this.goTo(this.currentIndex + 1);
      else this.goTo(this.currentIndex);
    }
  }

  customElements.define('product-fullscreen-slider', ProductFullscreenSlider);
}
